import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, CircleCheckBig, Loader2, Sparkles, UserRound } from "lucide-react";
import { assignSalon, fetchAgentSalons, fetchAssignHints } from "@/lib/bf";
import { fetchUsers } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const nm = (a) => a.name || a.email;

const AllDone = ({ onClose }) => (
  <>
    <DialogHeader><DialogTitle className="sr-only">Saloane fără agent</DialogTitle><DialogDescription className="sr-only">Toate saloanele au agent</DialogDescription></DialogHeader>
    <CircleCheckBig size={64} />
    <h3 data-testid="bf-assign-empty">Toate saloanele au agent</h3>
    <p>Niciun salon înscris nu mai așteaptă să fie alocat.</p>
    <button type="button" className="st-btn st-btn--light" onClick={onClose} data-testid="bf-assign-close">Închide</button>
  </>
);

export default function BFAssignPanel({ open, onOpenChange, onDone }) {
  const [rows, setRows] = useState(null);
  const [agents, setAgents] = useState([]);
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState("");
  const [hints, setHints] = useState({});
  const [counts, setCounts] = useState({});
  useEffect(() => {
    if (!open) return;
    setRows(null); setSel([]);
    fetchAgentSalons("").then(setRows).catch(() => setRows([]));
    fetchAssignHints().then((d) => { setHints(d.hints || {}); setCounts(d.counts || {}); }).catch(() => {});
    fetchUsers().then((d) => setAgents(d.users.filter((u) => (u.role === "agent" || u.role === "admin") && u.share_slug))).catch(() => {});
  }, [open]);
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const give = async (a) => {
    setBusy(a.share_slug);
    const done = [];
    for (const id of sel) { await assignSalon(id, a.share_slug).then(() => done.push(id)).catch(() => {}); } // eslint-disable-line no-await-in-loop
    setBusy("");
    const names = rows.filter((r) => done.includes(r.id)).map((r) => r.salon || "Salon").join(", ");
    toast[done.length === sel.length ? "success" : "error"](`${names} → ${nm(a)}`);
    setRows((l) => l.filter((r) => !done.includes(r.id)));
    setCounts((c) => ({ ...c, [a.share_slug]: (c[a.share_slug] || 0) + done.length }));
    setSel([]);
    window.dispatchEvent(new Event("bf-unassigned-refresh"));
    window.dispatchEvent(new Event("bf-moves-refresh"));
    onDone?.();
  };
  const agentName = (slug) => nm(agents.find((a) => a.share_slug === slug) || { name: slug });
  const sugg = sel.map((id) => hints[id]?.agent).filter(Boolean);
  const empty = rows && !rows.length;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`bf-assignpanel${empty ? " bf-assign-done" : ""}`} data-testid="bf-assign-panel">
        {empty ? <AllDone onClose={() => onOpenChange(false)} /> : (
          <>
            <DialogHeader>
              <DialogTitle>Saloane fără agent</DialogTitle>
              <DialogDescription>Bifează unul sau mai multe saloane în stânga, apoi apasă pe agentul din dreapta căruia i le dai. Agentul recomandat e marcat cu auriu.</DialogDescription>
            </DialogHeader>
            <div className="bf-assign-grid">
              <ul className="bf-assign-salons" data-testid="bf-assign-salons">
                {!rows && <li className="st-hint"><Loader2 size={14} className="animate-spin" /> Se încarcă…</li>}
                {(rows || []).map((r) => (
                  <li key={r.id}><button type="button" className={sel.includes(r.id) ? "is-on" : ""} onClick={() => toggle(r.id)} aria-pressed={sel.includes(r.id)} data-testid={`bf-assign-salon-${r.id}`}>
                    <i>{sel.includes(r.id) && <Check size={13} />}</i>
                    <span><b>{r.salon || "Salon"}</b><small>{[r.admin_name, r.city, r.phone].filter(Boolean).join(" · ")}</small>
                      {hints[r.id] && <em className="bf-assign-hint" data-testid={`bf-assign-hint-${r.id}`}><Sparkles size={11} /> Recomandat: {agentName(hints[r.id].agent)} · {hints[r.id].why}</em>}</span>
                  </button></li>
                ))}
              </ul>
              <div className="bf-assign-agents" data-testid="bf-assign-agents">
                <p className="st-label">{sel.length ? `Dă ${sel.length} ${sel.length === 1 ? "salon" : "saloane"} lui:` : "Alege întâi un salon"}</p>
                {agents.map((a) => (
                  <button key={a.share_slug} type="button" className={sugg.includes(a.share_slug) ? "is-sugg" : ""} disabled={!sel.length || !!busy} onClick={() => give(a)} data-testid={`bf-assign-agent-${a.share_slug}`}>
                    {busy === a.share_slug ? <Loader2 size={15} className="animate-spin" /> : <UserRound size={15} />}<span>{nm(a)}</span>
                    <b className="bf-assign-count" title="Saloane înscrise alocate deja" data-testid={`bf-assign-count-${a.share_slug}`}>{counts[a.share_slug] || 0}</b>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
